import { useState } from "react";
import { Box, Typography, Chip, Stack, Button, TextField } from "@mui/material";
import {
  Box as BoxIcon,
  Cog,
  UserCircle,
  Calendar,
  FilePlus,
  DollarWaves,
} from "@mynaui/icons-react";

const getPriorityStyles = (priority) => {
  switch (priority) {
    case "High":
      return {
        chipBg: "#FEF2F2",
        chipColor: "#C10007",
        chipBorder: "1px solid #C10007",
      };
    case "Medium":
      return {
        chipBg: "#FEF3C6",
        chipColor: "#E17100",
        chipBorder: "1px solid #E17100",
      };
    case "Low":
      return {
        chipBg: "#DCFCE7",
        chipColor: "#00A63E",
        chipBorder: "1px solid #00A63E",
      };
    default:
      return {
        chipBg: "#F3F4F6",
        chipColor: "#374151",
        chipBorder: "1px solid #374151",
      };
  }
};

const RequestDetail = ({ request, onApprove, onReject }) => {
  const [comment, setComment] = useState("");
  const priorityStyles = getPriorityStyles(request.priority);

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: { xs: "20px", md: "32px" },
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Header: Icon + ID chip + Title + Priority */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left: Icon + ID + Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <BoxIcon size={24} style={{ flexShrink: 0 }} />
            <Chip
              label={request.requestNumber || request.id}
              sx={{
                bgcolor: "#45556C",
                color: "#fff",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "24px",
                borderRadius: "8px",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "18px", md: "20px" },
                lineHeight: "28px",
                color: "#020618",
              }}
            >
              {request.title}
            </Typography>
          </Box>

          {/* Right: Priority chip */}
          <Chip
            label={request.priority}
            sx={{
              bgcolor: priorityStyles.chipBg,
              color: priorityStyles.chipColor,
              border: priorityStyles.chipBorder,
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: 0.6,
              height: "20px",
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Breadcrumb */}
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#62748E",
          }}
        >
          {request.breadcrumb}
        </Typography>
      </Box>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "22px",
          color: "#020618",
        }}
      >
        {request.description}
      </Typography>

      {/* Info cards: Asset + Assigned To */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <InfoCard
          icon={<Cog size={30} color="#020618" />}
          label="Asset"
          value={request.asset}
        />
        <InfoCard
          icon={<UserCircle size={30} color="#020618" />}
          label="Assigned To"
          value={request.assignedTo}
        />
      </Box>

      {/* Info cards: Due Date + Created */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <InfoCard
          icon={<Calendar size={30} color="#020618" />}
          label="Due Date"
          value={request.dueDate}
        />
        <InfoCard
          icon={<FilePlus size={30} color="#020618" />}
          label="Created"
          value={request.createdDate}
        />
      </Box>

      {/* Estimated Cost + Parts Required */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
        }}
      >
        {/* Estimated Cost card */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "0 0 280px" },
            height: "200px",
            background: "#F7FEE7",
            border: "1px solid #9AE600",
            borderRadius: "12px",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flext-start",
              gap: 1.75,
            }}
          >
            <DollarWaves size={48} color="#020618" stroke={1} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#020618",
                  lineHeight: "24px",
                }}
              >
                Estimated Cost
              </Typography>
              <Typography
                sx={{
                  fontSize: "36px",
                  fontWeight: 600,
                  color: "#020618",
                  lineHeight: "40px",
                }}
              >
                ${request.cost.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Parts Required table */}
        <Box
          sx={{
            flex: 1,
            height: "200px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            overflow: "hidden",
            background: "#F8FAFC",
          }}
        >
          {/* Parts header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "12px 18px 4px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#020618",
              }}
            >
              Parts Required ({request.parts})
            </Typography>
          </Box>

          {/* Parts rows */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "0 18px",
              ...(request.partsList.length > 4 && {
                maxHeight: "300px",
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: "0.5px" },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E1 transparent",
              }),
            }}
          >
            {request.partsList.map((part, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom:
                    index < request.partsList.length - 1
                      ? "1px solid #E2E8F0"
                      : "none",
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "16px",
                    color: "#020618",
                    lineHeight: "24px",
                    fontWeight: 500,
                  }}
                >
                  {part.name} x {part.quantity}
                </Typography>
                <Typography
                  sx={{
                    flexShrink: 0,
                    fontSize: "16px",
                    color: "#020618",
                    lineHeight: "24px",
                    fontWeight: 500,
                    textAlign: "right",
                  }}
                >
                  $ {part.cost.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Add Comments */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "24px",
            color: "#020618",
          }}
        >
          Add Comments
        </Typography>
        <TextField
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: "14px",
              "& fieldset": {
                borderColor: "#E2E8F0",
              },
              "&:hover fieldset": {
                borderColor: "#CBD5E1",
              },
            },
          }}
        />
      </Box>

      {/* Approve / Reject Buttons */}
      <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
        <Button
          variant="outlined"
          sx={{
            flex: 1,
            height: 48,
            background: "#F0FDF4",
            border: "1px solid #008236",
            color: "#008236",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "15px",
            textTransform: "none",
            "&:hover": {
              border: "1px solid #008236",
              background: "#F0FDF4",
            },
          }}
          onClick={() => onApprove(request.id, comment)}
        >
          Approve
        </Button>
        <Button
          variant="outlined"
          sx={{
            flex: 1,
            height: 48,
            background: "#FEF2F2",
            border: "1px solid #C10007",
            color: "#C10007",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "15px",
            textTransform: "none",
            "&:hover": {
              border: "1px solid #C10007",
              background: "#FEF2F2",
            },
          }}
          onClick={() => onReject(request.id, comment)}
        >
          Reject
        </Button>
      </Stack>
    </Box>
  );
};

// Reusable info card subcomponent
const InfoCard = ({ icon, label, value }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      border: "1px solid #E2E8F0",
      borderRadius: "12px",
      padding: "16px 20px",
    }}
  >
    {icon}
    <Box>
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          color: "#62748E",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          color: "#020618",
          fontWeight: 500,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

export default RequestDetail;
